import { AudioLines, ChevronRight, Upload, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import type { Recording } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate, formatDuration } from "@/lib/format";

/**
 * UI-4 — every recording, and where the pipeline has got each one to.
 *
 * The status is the newest run's, read again every couple of seconds while anything is
 * still moving (see `useRecordings`). Nothing on a row is invented: a recording that
 * has not been listened to yet has no duration and no participant count, and says so
 * by leaving them out rather than by showing a zero.
 *
 * Searching happens in the page rather than at the Server. The whole list is already
 * here — the navigator panel shows the same rows — and a round trip per keystroke
 * would be slower and no more correct at the scale one person's meetings reach.
 */
export function RecordingsPage() {
  const { recordings, recordingsLoading, recordingsProblem } = useApp();
  const [search, setSearch] = useState("");

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return recordings;

    return recordings.filter((recording) => nameOf(recording).toLowerCase().includes(needle));
  }, [recordings, search]);

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-wrap items-start justify-between gap-[var(--space-4)]">
        <div className="flex flex-wrap items-baseline gap-[var(--space-4)]">
          <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            Recordings
          </h1>
          <p className="max-w-[64ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">
            Capture meetings, calls and voice memos — track them through the pipeline.
          </p>
        </div>

        <Link
          to="/add/audio"
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110"
        >
          <Upload size={15} strokeWidth={2.5} />
          Upload recording
        </Link>
      </header>

      <div className="relative">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search recordings…"
          aria-label="Search recordings"
          className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-4 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none backdrop-blur-[var(--blur-sm)] placeholder:text-[var(--text-muted)] focus:border-[var(--ring)]"
        />

        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear the search"
            className="absolute right-1.5 top-1/2 flex h-[22px] w-[22px] -translate-y-1/2 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X size={12} strokeWidth={2.5} />
          </button>
        )}
      </div>

      {recordingsProblem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {recordingsProblem}
        </p>
      )}

      {recordingsLoading ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : shown.length === 0 ? (
        <Empty searching={search.trim().length > 0} />
      ) : (
        <ul aria-label="Recordings" className="flex flex-col gap-[var(--space-2)]">
          {shown.map((recording) => (
            <li key={recording.id}>
              <Row recording={recording} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ pieces

function Row({ recording }: { recording: Recording }) {
  const status = describe(recording.status);

  return (
    <Link
      to={`/recordings/${recording.id}`}
      className="flex items-center gap-[var(--space-4)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-2)] backdrop-blur-[var(--blur-sm)] transition hover:border-[var(--border-default)]"
    >
      {/* One icon, because there is one way in. Teams capture (CAP-5) is the other, and
          gets its own icon when it exists rather than a choice with one option. */}
      <Upload size={18} strokeWidth={2} className="shrink-0" style={{ color: status.color }} aria-hidden />

      <div className="min-w-0 flex-1 py-1">
        <p className="truncate text-[length:var(--text-md)] font-semibold text-[var(--text-primary)]">
          {nameOf(recording)}
        </p>
        <p className="mt-0.5 truncate text-[length:var(--text-xs)] text-[var(--text-muted)]">{metaOf(recording)}</p>
      </div>

      {recording.language && (
        <span className="shrink-0 rounded-[var(--radius-full)] border border-[var(--border-subtle)] px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-secondary)]">
          {recording.language}
        </span>
      )}

      <StatusPill status={recording.status} />

      <ChevronRight size={16} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" aria-hidden />
    </Link>
  );
}

export function StatusPill({ status }: { status: Recording["status"] }) {
  const described = describe(status);

  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-[var(--radius-full)] px-2.5 py-1 text-[length:var(--text-xs)] font-semibold",
        described.strong ? "border" : "bg-[var(--surface-2)] text-[var(--text-secondary)]",
      )}
      style={
        described.strong
          ? {
              color: described.color,
              borderColor: `color-mix(in oklch, ${described.color} 40%, transparent)`,
              background: `color-mix(in oklch, ${described.color} 16%, transparent)`,
            }
          : undefined
      }
    >
      {/* Muted unless the state is one of the two the pill draws in colour. The
          handoff's pill has three states — ready, error, pending — and queued,
          processing and cancelled are all the third; the row's icon is where the
          status colour appears for those. */}
      <span
        className="h-1.5 w-1.5 rounded-[var(--radius-full)]"
        style={{ background: described.strong ? described.color : "var(--text-muted)" }}
        aria-hidden
      />
      {described.label}
    </span>
  );
}

function Empty({ searching }: { searching: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] bg-[var(--surface-glass)] px-[var(--space-8)] py-[var(--space-12)] text-center backdrop-blur-[var(--blur-sm)]">
      <div className="mb-[var(--space-4)] flex h-12 w-12 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)]">
        <AudioLines size={22} strokeWidth={2} className="text-[var(--text-muted)]" />
      </div>

      {searching ? (
        <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
          No recording matches that
        </p>
      ) : (
        <>
          <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
            No recordings yet
          </p>
          <p className="mt-1 max-w-[46ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            Drop in a meeting from your recorder and the worker transcribes and diarizes it on your own
            machine.
          </p>
          <Link
            to="/add/audio"
            className="mt-[var(--space-4)] inline-flex h-9 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110"
          >
            <Upload size={14} strokeWidth={2.5} />
            Upload recording
          </Link>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ describing

/**
 * The five states the design draws, and the one it does not.
 *
 * "Not started" is a recording with no run at all — uploaded before there was a
 * pipeline. It is not queued, and saying so would be a small lie the owner could act
 * on, so it reads as what it is and the source page is where it can be started.
 */
function describe(status: Recording["status"]): { label: string; color: string; strong: boolean } {
  switch (status) {
    case "processing":
      return { label: "Processing", color: "var(--vl-bridge)", strong: false };
    case "done":
      return { label: "Done", color: "var(--success)", strong: true };
    case "failed":
      return { label: "Failed", color: "var(--destructive)", strong: true };
    case "cancelled":
      return { label: "Cancelled", color: "var(--text-muted)", strong: false };
    case "queued":
      return { label: "Queued", color: "var(--text-muted)", strong: false };
    default:
      return { label: "Not started", color: "var(--text-muted)", strong: false };
  }
}

/** The colour a status dot takes in the navigator panel, for the same six states. */
export function statusColour(status: Recording["status"]): string {
  return describe(status).color;
}

export function nameOf(recording: Recording): string {
  return recording.title ?? recording.originalFilename ?? "Untitled recording";
}

/**
 * "24 Jul 2026 · 18m · 2 participants", with whatever is not known yet left out
 * entirely rather than shown as a placeholder. A recording still being transcribed
 * genuinely has no duration, and an em dash in its place reads as a value.
 */
function metaOf(recording: Recording): string {
  const date = formatDate(recording.sourceDate ?? recording.createdAt);
  const duration = formatDuration(recording.durationMs);
  const speakers =
    recording.speakerCount === null
      ? ""
      : `${recording.speakerCount} participant${recording.speakerCount === 1 ? "" : "s"}`;

  return [date, duration, speakers].filter(Boolean).join(" · ");
}
